<template>
	<div class="upsell-rules-container" data-gtm-upsell-rules-container="true">
		<template v-if="loading">
			<v-layout align-center column>
				<div class="thanks-message">{{$t("shopComponents.gt2.subscriptionPlansWrapper.refreshing")}}</div>
				<v-progress-circular indeterminate color="white"></v-progress-circular>
			</v-layout>
		</template>
		<v-container v-else>
			<v-layout class="top-text">
				<h2>{{ $t(`shopComponents.defaultGlobalTemplate.upsellModal.title`) }}</h2>
			</v-layout>
			<v-layout class="checkbox-list flex-wrap">
				<v-flex
					v-for="product in upsellProducts"
					md4
					class="list-container p-3"
					:key="product.id"
					@click="checkSelectedProducts(product.id)"
					:class="{'selected': selectedUpsellProducts.includes(product.id)}"
				>
					<template v-if="isNotMobile">
						<div :style="'background-image: url(' + getProductImage(product) + ')'" class="upsell-bg-image">
							<img :src="getProductImage(product)" alt="" :class="{'border-yellow': selectedUpsellProducts.includes(product.id)}">
							<LabelComponent
								:product="product"
								parentComponent="upsell"
								v-if="productHasCustomLabelFn(product)"
							/>
							<div
								class="product"
								:class="{'border-yellow': selectedUpsellProducts.includes(product.id)}"
								:data-gtm-upsell-product="selectedUpsellProducts.includes(product.id)"
							>
								<p class="product-name" data-gtm-product-name="true">{{product.name | translate(`product.${product.id}.name`)}}</p>
								<div class="product-text">
									<p v-if="!isBM100" class="product-ingredients">
										{{product.description | translate(`product.${product.id}.description`)}}
									</p>
									<div v-if="!isBM100" class="pt-2">
										<p class="product-price product-price-cj">{{ $t(`shopComponents.defaultGlobalTemplate.upsellModal.only`) }}</p>
										<p class="product-price" data-gtm-product-price="true">{{formatPriceZero(discountPrice(product))}}</p>
									</div>
									<div v-else :class="product.name.length < 25 ? 'pt-3' : ''">
										<p class="product-price" data-gtm-product-price="true">{{formatPriceZero(discountPrice(product))}}</p>
									</div>
								</div>
							</div>
						</div>
					</template>
					<template v-else>
						<div class="upsell-bg-image">
							<div v-if="productHasCustomLabelFn(product)">
								<LabelComponent
									:product="product"
									parentComponent="upsell"
								>
									<img :src="getProductImage(product)" alt="" :class="{'border-yellow': selectedUpsellProducts.includes(product.id)}">
								</LabelComponent>
								</div>
							<template v-else>
								<img :src="getProductImage(product)" alt="" :class="{'border-yellow': selectedUpsellProducts.includes(product.id)}">
							</template>
							<div
								class="product"
								:class="{'border-yellow': selectedUpsellProducts.includes(product.id)}"
								:data-gtm-upsell-product="selectedUpsellProducts.includes(product.id)"
							>
								<p class="product-name" data-gtm-product-name="true">{{product.name | translate(`product.${product.id}.name`)}}</p>
								<div class="product-text">
									<p v-if="!isBM100" class="product-ingredients">
										{{product.description | translate(`product.${product.id}.description`)}}
									</p>
									<div v-if="!isBM100" class="pt-2">
										<p class="product-price product-price-cj">{{ $t(`shopComponents.defaultGlobalTemplate.upsellModal.only`) }}</p>
										<p class="product-price" data-gtm-product-price="true">{{formatPriceZero(discountPrice(product))}}</p>
									</div>
									<div v-else :class="product.name.length < 25 ? 'pt-3' : ''">
										<p class="product-price" data-gtm-product-price="true">{{formatPriceZero(discountPrice(product))}}</p>
									</div>
								</div>
							</div>
						</div>
					</template>
				</v-flex>
				<div class="no-upsell-products" v-if="!upsellProducts.length">{{ $t(`shopComponents.defaultGlobalTemplate.upsellModal.noProducts`) }}</div>
			</v-layout>
			<v-layout class="buttons-container pt-3" :class="isBM100 ? 'justify-content-center' : ''">
				<v-flex md6 class="px-1">
					<v-btn
						class="back-btn cancel full-width"
						@click="closeDialog"
						data-cy="upsell-cancel-button"
						:key="isUpsellProductAdded"
					>
						{{ closeButtonText }}
					</v-btn>
				</v-flex>
				<v-flex v-if="!isBM100" md6 class="px-1">
					<v-btn class="add-btn add full-width" @click="addToBasket" data-gtm-add-to-basket-upsell="true">
						{{ $t(`shopComponents.defaultGlobalTemplate.upsellModal.addButton`) }}
					</v-btn>
				</v-flex>
			</v-layout>
		</v-container>
	</div>
</template>

<script>
	import {mapGetters} from "vuex";
	import {canBePickedUp, copyObject, forEachAsync, RCMSourceType} from "../../../libraries/utils";
	import ProductSelection from "../../../domains2/orders/ProductSelection";
	import * as actions from "../../../store/mutation-types";
	import _ from "lodash";
	import * as Util from "../../../libraries/utils";
	import {formatPriceZero} from "../../../domains2/libraries/Utils";
	import {getProductPriceWithDiscounts} from "../../../libraries/globalTemplateSpecific/bm100/utils";
	import appConfig from "../../../appconfig";
	import * as Utils from "../../../libraries/utils";
	import {productHasCustomLabel} from "../../../helper/ProductHelper";
	import LabelComponent from "../../UtilityComponents/Label";
	import RecommendationEngineProvider from "../../../providers/RecommendationEngineProvider";
	import {DeliveryMethodsEnum} from "../../../domains2/libraries/DeliveryMethods";
	import * as mutationTypes from "../../../store/mutation-types";
	import {getProductIdsFromOrderLines} from "../../../helper/ProductSelectionHelper";
	import pricesMixin from "../../../mixins/prices.mixin";
	import OrderHelperObject from "../../../domains2/orders/OrderHelperObject";
	import analyticsDataMixin from "../../../mixins/analyticsData.mixin";
	import productSelectionMixin from "../../../mixins/productSelection.mixin";

	export default {
		name: "UpsellRules",
		props: ["onCloseDialog"],
		mixins: [pricesMixin, analyticsDataMixin, productSelectionMixin],
		data() {
			return {
				selectedUpsellProducts: [],
				upsellProducts: [],
				recommendationsWithBuffer: [],
				loading: false,
				isUpsellProductAdded: false
			};
		},
		components: {LabelComponent},
		computed: {
			...mapGetters([
				"featureToggles",
				"productCatalog",
				"upsellRulesProducts",
				"cartProducts",
				"orderLines",
				"globalSelectedShop",
				"upsellRulesMaximumNumber",
				"tenantSpecificData",
				"recommendedProducts",
				"pickedRecommendedProducts",
				"tenantSettings",
				"useRecommendations",
				"experimentId"
			]),
			closeButtonText() {
				return !this.isUpsellProductAdded
					? this.$t(`shopComponents.defaultGlobalTemplate.upsellModal.closeButton`)
					: this.$t(`shopComponents.defaultGlobalTemplate.upsellModal.goToPayment`);
			},
			staticPath() {
				return Utils.getStaticPath();
			},
			isNotMobile() {
				return !(appConfig.channelAlias() === "mobileBrowser" || appConfig.channelAlias(0) === "mobileApp");
			},
			isBM100() {
				return appConfig.isBM100();
			}
		},
		async mounted() {
			const recommendedProductsMap = {};
			this.loading = true;

			if(this.useRecommendations) {
				const productToRcm = getProductIdsFromOrderLines(this.orderLines).map(id => {
					try {
						const product = this.productCatalog.getProduct(id);
						return {
							itemNumber: product.id,
							name: product.name
						};
					} catch (e) {
						// product is not available, should be ignored
						return false;
					}
				}).filter(Boolean);

				const {recommendations, buffer, experimentId} = await RecommendationEngineProvider.getRecommendations({
					deliveryMethod: Object.keys(DeliveryMethodsEnum)
						.find(key => DeliveryMethodsEnum[key] === this.tenantSpecificData.deliveryChoice),
					products: productToRcm,
					shopName: this.globalSelectedShop.shopNameAlias,
					channel: appConfig.channelAlias(),
					experimentType: "CrossSell"
				}, appConfig.tenantId);
				this.$store.commit(mutationTypes.SET_EXPERIMENT_ID, `${experimentId}`);
				const rawRecommendationsWithBuffer = [
					...(recommendations ? recommendations : []),
					...(buffer ? buffer : [])
				];
				this.recommendationsWithBuffer = rawRecommendationsWithBuffer;
				const recommendedProducts = rawRecommendationsWithBuffer.map(item => {
					// returned products could not be available in Bifrost
					// these should be skipped
					try {
						const product = this.productCatalog.getProduct(item.itemNumber);
						const isProductAvailable = canBePickedUp(
							product,
							this.productCatalog,
							this.$store.getters.cachedPickupRules,
							this.globalSelectedShop.id
						);
						return isProductAvailable ? product : undefined;
					} catch (e) {
						return undefined;
					}
				})
					.filter(Boolean);
				// we need a map to match the format needed bellow
				recommendedProducts.forEach(item => recommendedProductsMap[item.id] = null);
			}
			const self = this;
			const upselling = this.useRecommendations ? recommendedProductsMap : self.upsellRulesProducts;
			const maximumNumber = this.useRecommendations ? (this.tenantSettings.maxRecommendedProducts || 6) : self.upsellRulesMaximumNumber;
			const cartProductIds = self.cartProducts.map(({ productId }) => productId);
			const cartProductDepartments = [];
			cartProductIds.forEach((i) => cartProductDepartments.push(self.productCatalog.getProduct(i).departmentId));
			const cartProductIdsSet = new Set(cartProductIds);
			const upsellProductsForSell = [];
			for (const productId in upselling) {
				const productDepartments = upselling[productId];
				if (!cartProductIdsSet.has(productId) && (!productDepartments || !productDepartments.length || _.intersection(cartProductDepartments, productDepartments).length)) {
					upsellProductsForSell.push(productId);
				}
			}
			this.loading = false;
			self.convertProductIdsToProducts(upsellProductsForSell, maximumNumber);
			self.selectedUpsellProducts = [];
		},
		methods: {
			formatPriceZero,
			productHasCustomLabelFn(product){
				return productHasCustomLabel(product);
			},
			getProductImage(product, isKiosk){
				const specificProduct = product.id
					? product
					: this.catalogProducts
						.find(element => element.id === product.id_ref);

				// taking only first image due to design breaking with multiple images available
				// getBackImages should order based on is it kiosk or not
				return Util.getBackImages(specificProduct, isKiosk).slice(0, 1);
			},
			checkSelectedProducts(id) {
				const self = this;
				if(self.selectedUpsellProducts.includes(id)) {
					if (!this.featureToggles.verticalKioskBasket) {
						self.selectedUpsellProducts = self.selectedUpsellProducts.filter( (x) => x !== id);
					}
				} else {
					self.selectedUpsellProducts.push(id);
					if(self.isBM100) {
						self.addToBasket();
						if(this.useRecommendations) {
							this.$store.commit(
								mutationTypes.SET_PICKED_RECOMMENDED_PRODUCTS,
								[...this.pickedRecommendedProducts, this.recommendedProducts.find(p => p.itemNumber === id)]
							);
						}
						if (!this.featureToggles.verticalKioskBasket) {
							self.upsellProducts = self.upsellProducts.filter((p) => {
								return p.id !== id;
							});
						}
						if(!this.featureToggles.verticalKioskBasket && self.upsellProducts.length === 0) {
							this.onCloseDialog();
						}
						if (this.featureToggles.verticalKioskBasket && self.upsellProducts.length === self.selectedUpsellProducts.length) {
							this.onCloseDialog();
						}
					}
				}
			},
			copyObject,
			closeDialog() {
				if(this.useRecommendations && this.recommendedProducts.length && this.experimentId) {
					RecommendationEngineProvider.sendFeedback({
						recommendations: this.recommendedProducts,
						picks: this.pickedRecommendedProducts,
						channel: appConfig.channelAlias(),
						shopName: this.globalSelectedShop.shopNameAlias,
						experimentId: this.experimentId
					}, appConfig.tenantId);
					this.$store.commit(mutationTypes.SET_RECOMMENDED_PRODUCTS);
					this.$store.commit(mutationTypes.SET_PICKED_RECOMMENDED_PRODUCTS);
					this.$store.commit(mutationTypes.SET_EXPERIMENT_ID);
				}
				this.onCloseDialog();
			},
			async addToBasket() {
				this.$emit("addToBasketClicked", true);
				const self = this;
				if (!self.selectedUpsellProducts.length) {
					this.onCloseDialog();
					return;
				}
				const validProducts = self.selectedUpsellProducts.map((prodId) => {
					const item = self.productCatalog.getProduct(prodId);
					const productSelection = item.departmentId === 2
						? this.createComplexProductSelection(item)
						: ProductSelection.createFromProduct(item);
					try {
						const ol = this.generateOrderLines([productSelection]);
						const mainProduct = OrderHelperObject.mapToReceiptLines(ol, this.$i18n)[0];
						const productWithSubProducts = Utils.getGTMFlattenSingleProductSelection(mainProduct);
						const products = productWithSubProducts.length ? productWithSubProducts : [productSelection];
						Utils.pushGTMeCommerceData({
							eventName: "add_to_cart",
							products: products,
							value: Utils.getGTMTotalPrice(products),
							category: this.productCatalog.getDepartment(item.departmentId)
						});
					} catch (e) {
						// even if something here fails, we should proceed
					}
					return {
						productId: prodId,
						quantity: 1,
						groups: productSelection.groups,
						configurations: productSelection.configurations,
						forceNewInCart: true,
						productSelectionId: productSelection.productSelectionId,
						rcmSource: RCMSourceType.CrossSell
					};
				});
				await forEachAsync(validProducts, ps => {
					ps.forceNewInCart = true;
					return self.$store.dispatch(actions.ADD_TO_CART_SINGLE_ITEM, _.cloneDeep(ps));
				});
				await self.$store.dispatch(actions.CHECKOUT_RESET_WITH_LAST_UPDATE_TIME);
				if (!self.featureToggles.verticalKioskBasket) {
					self.selectedUpsellProducts = [];
				}
				if(!self.isBM100) {
					self.onCloseDialog();
				}
				this.isUpsellProductAdded = true;
			},
			convertProductIdsToProducts (ids, maxNumber) {
				const self = this;
				self.upsellProducts = [];
				ids.forEach((productId) => {
					const categoriesOfProduct = self.productCatalog.getAllCategoriesOfProduct(productId);
					const isProductCategoriesContainSelectedShop = categoriesOfProduct.some(
						(category) => Array.isArray(category.isSpecificForShops) && category.isSpecificForShops.includes(this.globalSelectedShop.id));
					const isProductSupportedByActiveChannel = !categoriesOfProduct.length || categoriesOfProduct.filter(category => !category.channels || category.channels.includes(appConfig.channelAlias())).length;
					// this code covers using only products which are assigned to categories for specified channel
					if(isProductSupportedByActiveChannel && isProductCategoriesContainSelectedShop) {
						self.upsellProducts.push(self.productCatalog.getProduct(productId));
					}
				});
				const productSelectionsFromUpsellProducts = self.upsellProducts.map(product => {
					const productSelection = ProductSelection.createFromProduct(product);
					return {
						...productSelection,
						quantity: 1
					};
				});
				const productPricesDict = getProductPriceWithDiscounts(self.upsellProducts, productSelectionsFromUpsellProducts, self.productCatalog);

				self.upsellProducts = self.upsellProducts.reduce((result, product) => {
					// this filters products based on delivery method
					if(product.options.deliveryMethodIndex) {
						if(!product.options.deliveryMethodIndex.includes(self.tenantSpecificData.deliveryChoice)) {
							return result;
						}
					}
					product.productPrice  = productPricesDict[product.id];
					return [...result, product];
				}, []);

				if (maxNumber && maxNumber < self.upsellProducts.length) {
					self.upsellProducts = this.useRecommendations
						? self.upsellProducts.slice(0, maxNumber)
						: _.shuffle(self.upsellProducts).slice(0, maxNumber);
				}
				if(!self.upsellProducts.length) {
					this.onCloseDialog();
				}
				if(this.useRecommendations && this.recommendationsWithBuffer.length) {
					this.$store.commit(mutationTypes.SET_RECOMMENDED_PRODUCTS, self.upsellProducts.map((p) => {
						const recommendedProduct = this.recommendationsWithBuffer.find(r => r.itemNumber === p.id);
						return {
							itemNumber: recommendedProduct.itemNumber,
							type: recommendedProduct.type
						};
					})
					);
				}
			}
		}
	};
</script>

<style scoped lang="scss">
	.justify-content-center {
		justify-content: center;
	}
	.list-container{
		padding: 10px 5px;
	}
	.background-surface {
		display: none;
	}
</style>
